import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { SDKConnected } from 'features/sdk/sdkStateSlice';
import { RootState } from 'store/store';
import { ToolbarOrientation, ToolbarPlacement, ToolbarState } from 'features/toolbar/types';
import { authorizeUser } from 'features/room/thunks/authorizeUser';
import { styleConstants } from 'theme/styleConstants';
import { selectIsSystemSession } from 'features/application/selectors';

export const initialState: ToolbarState = {
  visible: true,
  placement: 'left',
  systemPlacement: 'left',
  activeUserCount: 0,
  lockIconEnabled: false,
};

export const toolbarSlice = createSlice({
  name: 'toolbar',
  initialState,
  reducers: {
    placementChanged(state, action: PayloadAction<ToolbarPlacement>) {
      state.placement = action.payload;
    },
    toolbarVisibilityChanged(state, action: PayloadAction<boolean>) {
      state.visible = action.payload;
    },
    toolbarVisibilityToggled(state) {
      state.visible = !state.visible;
    },
    activeUserCountUpdated(state, action: PayloadAction<number>) {
      state.activeUserCount = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(authorizeUser.fulfilled, (state, action) => {
        state.placement = action.payload.settings.toolbarPosition;
        state.systemPlacement = action.payload.settings.toolbarPosition;
        state.lockIconEnabled = action.payload.settings.toolbarLockIconEnabled;
      })
      .addCase(SDKConnected, (state, action) => {
        const options = action.payload;

        if (options.showToolbar !== undefined) {
          state.visible = options.showToolbar;
        }
      });
  },
});

export const {
  placementChanged,
  toolbarVisibilityChanged,
  toolbarVisibilityToggled,
  activeUserCountUpdated,
} = toolbarSlice.actions;

export const selectToolbarPlacement = (state: RootState) => state.toolbar.placement;
export const selectToolbarSystemPlacement = (state: RootState) => state.toolbar.systemPlacement;

export const selectToolbarVisibility = (state: RootState) => state.toolbar.visible;

export const selectToolbarWidth = createSelector(
  [selectToolbarVisibility, selectIsSystemSession],
  (toolbarVisible, isSystemSession) => {
    // toolbar is always hidden in system session
    if (isSystemSession) {
      return 0;
    }

    return toolbarVisible ? styleConstants.toolbar.width : 0;
  }
);

export const selectToolbarOrientation = (state: RootState): ToolbarOrientation =>
  state.toolbar.placement === 'bottom' ? 'horizontal' : 'vertical';

export const selectToolbarUserCount = (state: RootState) => state.toolbar.activeUserCount;

export const selectToolbarLockIconEnabled = (state: RootState) => state.toolbar.lockIconEnabled;

export default toolbarSlice.reducer;
